import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { showError } from 'actions/actionCreators/snackbarActions';
import { withTranslation } from 'react-i18next';
import { withRights } from '../../../../../providers/RightsConsumer';
import { loadSignees } from '../actionCreators';
import { ATTACHMENT_AND_PRINT_GENERATED_STATUS_QUERY } from '../queries';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { GENERATE_ATTACHMENTS_AND_PRINTS_MUTATION } from '../mutations';
import { DOCUMENT_ATTACHMENT_AND_PRINTS_GENERATED, OFFER_STATE_UPDATED } from '../subscriptions';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { useMutation } from '@apollo/client';
import CloudDownload from '@mui/icons-material/CloudDownload';
import DocumentsExportContainer from './DocumentsExportContainer';
import DocumentsUploadContainer from './DocumentsUploadContainer';
import DocumentSigneesContainer from './DocumentSigneesContainer';
import { env } from 'utils/env';
import store from '../../../../../store/configureStore';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Button, Card, Dialog } from '@totalsoft/rocket-ui';
import { BackdropCustom, Qr } from './DocumentsStyles';

const url = env.REACT_APP_LEASING_API_URL;

const DocumentsContainer = ({ documentId, siteId, documentNumber, t, rights, clientDefaultEmail }) => {
  const [qrOpen, setQROpen] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const docUrl = useCallback(() => {
    const user = store.getState().oidc.user;
    if (user) {
      const token = user.access_token;
      return `${window.location.protocol}/${window.location.hostname}${
        window.location.port ? ':' + window.location.port : ''
      }/upload/${documentId}/${siteId}?access_token=${token}`;
    } else {
      return '';
    }
  }, [documentId, siteId]);

  const [generated, setGenerated] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);
  const [forceReload, setForceReload] = useState(false);
  const dispatch = useDispatch();

  const handleRestoreLoader = () => {
    setNeedsReload(true);
  };

  const signeesNumber = useSelector(
    state =>
      state.entities.documentSignees && state.entities.documentSignees[`${documentId}-${siteId}`]?.length
        ? state.entities.documentSignees[`${documentId}-${siteId}`]?.length
        : 0,
    shallowEqual
  );

  const {
    loading,
    data,
    refetch: refetchGeneratedStatus
  } = useQueryWithErrorHandling(ATTACHMENT_AND_PRINT_GENERATED_STATUS_QUERY, {
    variables: { documentId, siteId },
    fetchPolicy: 'network-only'
  });

  const [generateAttachmentsAndPrints] = useMutation(GENERATE_ATTACHMENTS_AND_PRINTS_MUTATION, {
    onError: error => {
      showError(error.message);
    }
  });

  useEffect(() => {
    if (!loading) {
      if (needsReload || data?.getAttachmentsAndPrintsGeneratedStatus?.attachmentsAndPrintsGeneratedStatus !== true) {
        generateAttachmentsAndPrints({
          variables: {
            input: {
              documentId,
              siteId
            }
          }
        });
      } else setGenerated(true);
    }
  }, [generateAttachmentsAndPrints, documentId, siteId, loading, data, needsReload]);

  useGlobalSubscription(DOCUMENT_ATTACHMENT_AND_PRINTS_GENERATED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: () => {
      setGenerated(true);
      if (needsReload) setForceReload(true);
      setNeedsReload(false);
    }
  });

  useGlobalSubscription(OFFER_STATE_UPDATED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: () => {
      refetchGeneratedStatus();
      handleRestoreLoader();
    }
  });

  useEffect(() => {
    dispatch(loadSignees(documentId, siteId));
  }, [documentId, siteId, dispatch]);

  const openQR = () => {
    setQROpen(true);
  };

  const handleQRClose = () => {
    setQROpen(false);
  };

  const openDocumentsSignees = () => {
    setShowModal(true);
  };

  const qr = () => {
    return (
      <span>
        <Qr onClick={openQR} src={`${url}/qr?url=${docUrl()}&size=1&access_token=${store.getState().oidc.user?.access_token}`} alt='qr' />
      </span>
    );
  };
  const qrBig = <img src={`${url}/qr?url=${docUrl()}&size=2&access_token=${store.getState().oidc.user?.access_token}`} alt='qr' />;

  return (
    <>
      {!loading && rights.documentsExportRead && (
        <Card
          icon={CloudDownload}
          title={t('OfferEdit.Documents.Export')}
          actions={
            rights.documentSigneesRead && (
              <Button sx={{ mt: 0.5, mr: 1 }} onClick={openDocumentsSignees}>
                {`${t('OfferEdit.Documents.Signees.Title')} (${signeesNumber})`}
              </Button>
            )
          }
        >
          <DocumentsExportContainer
            documentId={documentId}
            siteId={siteId}
            generated={generated}
            forceReloadDocuments={forceReload}
            clientDefaultEmail={clientDefaultEmail}
          />
        </Card>
      )}

      {showModal && <DocumentSigneesContainer documentId={documentId} siteId={siteId} showModal={showModal} setShowModal={setShowModal} />}

      {!loading && rights.uploadDocumentRead && (
        <Card icon={qr} title={t('OfferEdit.Documents.Upload')}>
          <DocumentsUploadContainer
            documentId={documentId}
            siteId={siteId}
            documentNumber={documentNumber}
            generated={generated}
            forceReloadDocuments={forceReload}
          />
        </Card>
      )}
      <Dialog
        id='qr-dialog'
        open={qrOpen}
        onClose={handleQRClose}
        BackdropComponent={BackdropCustom}
        contentProps={{ sx: { display: 'flex', justifyContent: 'center' } }}
        content={qrBig}
      />
    </>
  );
};

DocumentsContainer.propTypes = {
  t: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  documentNumber: PropTypes.string.isRequired,
  siteId: PropTypes.number.isRequired,
  rights: PropTypes.object.isRequired,
  clientDefaultEmail: PropTypes.string
};

export default withTranslation()(withRights(DocumentsContainer));
